// Sprint mobile-drawer-swipe-open-fix (2026-05-13) — restores swipe-to-open
// on the mobile drawer after PR #244 shipped vaul without an opener
// listener. Vaul does NOT portal-mount the drawer node until `open` flips
// to `true` at least once (see `DashboardLayout.test.tsx:110-112`), so a
// closed drawer has no DOM surface to drag. This hook is the missing
// opener: a window-level pointer listener that flips `open` to `true`
// when a horizontal-dominant rightward swipe exceeds the threshold.
//
// Closing is handled by vaul natively (finger-following drag on the
// already-mounted panel + snap on release). This hook is opener-only.
//
// Differs from the deleted `useSwipeDrawer`:
//   • No 16 px edge restriction — swipe-from-anywhere per the predecessor
//     spec's acceptance criterion #1 ("swipe-right from anywhere in the
//     viewport").
//   • No close path — vaul owns close-with-finger now.
//
// SSR-safe (no-op outside `window`). Mobile-only (caller passes
// `enabled = !isDesktop`).

import { useEffect } from 'react';

const THRESHOLD_PX = 60;
const HORIZONTAL_DOMINANCE = 1;

export interface UseSwipeOpenDrawerOptions {
  /** When true, the drawer is already open — listener is inert. */
  open: boolean;
  /** Called when a qualifying swipe is detected. */
  onOpen: () => void;
  /** When false, the gesture is inert — used to disable on desktop. */
  enabled?: boolean;
}

export function useSwipeOpenDrawer({
  open,
  onOpen,
  enabled = true,
}: UseSwipeOpenDrawerOptions): void {
  useEffect(() => {
    if (!enabled) return;
    if (open) return;
    if (typeof window === 'undefined') return;

    let startX: number | null = null;
    let startY: number | null = null;

    function reset() {
      startX = null;
      startY = null;
    }

    function onPointerDown(e: PointerEvent) {
      if (e.pointerType === 'mouse') return;
      startX = e.clientX;
      startY = e.clientY;
    }

    function onPointerMove(e: PointerEvent) {
      if (startX === null || startY === null) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      if (adx < THRESHOLD_PX) return;
      if (adx <= ady * HORIZONTAL_DOMINANCE) {
        reset();
        return;
      }
      if (dx > 0) {
        reset();
        onOpen();
        return;
      }
      reset();
    }

    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', reset);
    window.addEventListener('pointercancel', reset);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', reset);
      window.removeEventListener('pointercancel', reset);
    };
  }, [open, onOpen, enabled]);
}
